import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ListSection } from '../components/ios';

// iOS inset-grouped content row inside a ListSection card.
function Block({ title, children }) {
  return (
    <div className="px-4 py-3">
      {title && <h4 className="font-semibold text-gray-900 mb-2 text-[15px]">{title}</h4>}
      {children}
    </div>
  );
}

function BulletList({ items, bulletClass = 'text-[#8E8E93]' }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start">
          <span className={`${bulletClass} mr-2`}>•</span>
          <span className="text-[15px] text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: back chevron + centered title */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center -ml-2 text-[#FF3B30] active:opacity-60"
            aria-label="Back"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            Terms of Service
          </h1>
          <span className="w-6" />
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        <p className="px-8 mt-4 text-[13px] text-[#6D6D72]">Last Updated: November 3, 2024</p>

        <ListSection title="1. Acceptance of Terms">
          <Block>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              By accessing and using the CheF mobile application ("the App"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms of Service, please do not use the App.
            </p>
          </Block>
        </ListSection>

        <ListSection title="2. Description of Service">
          <Block>
            <p className="text-[15px] text-gray-700 leading-relaxed">
              CheF is a food delivery platform operated by Browne Ink that connects customers with CheF restaurant locations in St. Kitts and Nevis. The App facilitates food ordering, payment processing, and delivery coordination between customers, restaurants, and independent delivery drivers.
            </p>
          </Block>
        </ListSection>

        <ListSection title="3. User Accounts">
          <Block title="3.1 Registration">
            <p className="text-[15px] text-gray-700">
              You must create an account to use certain features of the App. You agree to provide accurate, current, and complete information during registration.
            </p>
          </Block>
          <Block title="3.2 Account Security">
            <p className="text-[15px] text-gray-700">
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
          </Block>
          <Block title="3.3 Account Information">
            <p className="text-[15px] text-gray-700">
              Your name and email address cannot be changed after registration. Only phone numbers and profile images may be updated.
            </p>
          </Block>
        </ListSection>

        <ListSection title="4. User Responsibilities">
          <Block title="4.1 Customers">
            <BulletList items={[
              'Provide accurate delivery addresses and contact information',
              'Be available to receive deliveries at the specified address',
              'Pay for orders in full through the App',
              'Treat delivery drivers with respect',
            ]} />
          </Block>
          <Block title="4.2 Restaurants">
            <BulletList items={[
              'Prepare orders accurately and in a timely manner',
              'Maintain food quality and safety standards',
              'Update menu availability in real-time',
              'Package food securely for delivery',
              'Accept or decline orders based on capacity',
            ]} />
          </Block>
          <Block title="4.3 Drivers">
            <BulletList items={[
              "Maintain valid driver's license and vehicle insurance",
              'Deliver orders promptly and safely',
              'Handle food with care and maintain hygiene',
              'Be within 100 feet of delivery address to complete delivery',
              'Complete all accepted orders before going offline',
            ]} />
          </Block>
        </ListSection>

        <ListSection title="5. Orders and Payments">
          <Block title="5.1 Order Placement">
            <p className="text-[15px] text-gray-700">
              All orders are subject to acceptance by the restaurant. Restaurants reserve the right to decline orders based on availability or capacity.
            </p>
          </Block>
          <Block title="5.2 Pricing">
            <p className="text-[15px] text-gray-700">
              Prices are set by the restaurant and displayed in the App. Delivery fees are calculated based on distance and displayed before order confirmation.
            </p>
          </Block>
          <Block title="5.3 Payment Processing">
            <p className="text-[15px] text-gray-700">
              Payments are processed securely through JADx Cash. By placing an order, you authorize us to charge your selected payment method.
            </p>
          </Block>
        </ListSection>

        <ListSection title="Contact Us">
          <Block>
            <p className="text-[15px] text-gray-700">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <div className="mt-2 space-y-1">
              <p className="text-[15px] text-gray-700"><strong>Email:</strong> browneinc7@gmail.com</p>
              <p className="text-[15px] text-gray-700"><strong>Phone:</strong> 1 869 660 1052</p>
            </div>
          </Block>
        </ListSection>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
